import type { Entity, Vec2 } from '@lakaran/geometry';
import type { CadDocument } from '@lakaran/engine';
import type { CadEditor } from '../editor';

/** Services a tool needs from the editor. The editor implements this itself. */
export interface ToolHost {
  readonly doc: CadDocument;
  readonly editor: CadEditor;
  prompt(text: string, options?: string[]): void;
  message(text: string): void;
  setDraft(entities: Entity[]): void;
  setReference(point: Vec2 | undefined): void;
  endTool(): void;
}

/** A numeric parameter exposed by a tool for its current step (dynamic input). */
export interface ToolField {
  id: string;
  label: string;
  unit: 'mm' | 'deg' | 'num';
  value: number;
  decimals: number;
  min?: number;
}

export abstract class Tool {
  abstract readonly name: string;
  abstract readonly title: string;
  abstract readonly aliases: readonly string[];

  protected host!: ToolHost;

  start(host: ToolHost): void {
    this.host = host;
    this.begin();
  }

  protected abstract begin(): void;

  /** Called with a snapped world point on click. */
  abstract point(p: Vec2): void;

  /** Called on pointer move for previews. */
  hover(_p: Vec2): void {}

  /** Typed command-line input. Return true when consumed. */
  input(_text: string): boolean {
    return false;
  }

  enter(): void {
    this.host.endTool();
  }

  /**
   * True for tools that keep collecting points (line, polyline, spline): a bare `Enter` then
   * finishes the shape instead of landing a point at the cursor. One-shot tools such as
   * rectangle or circle keep accepting the rubber-band size, which is their whole shape.
   */
  get enterFinishes(): boolean {
    return false;
  }

  cancel(): void {
    this.host.setDraft([]);
    this.host.endTool();
  }

  /** Anchor for perpendicular/tangent snaps. */
  get referencePoint(): Vec2 | undefined {
    return undefined;
  }

  /** Ctrl angle constraint applies to this tool's current step. */
  get supportsAngleConstraint(): boolean {
    return true;
  }

  /**
   * Numeric parameters for the current step, pre-filled from the rubber-band cursor.
   * Returning `null` means the step is picked with the pointer only.
   */
  getFields(_cursor: Vec2): ToolField[] | null {
    return null;
  }

  /** Applies committed field values; tools decide whether that completes a step. */
  applyFields(_values: Record<string, number>, _cursor: Vec2): void {}
}
